// client/src/components/BidEstimation/VendorQuotes/QuoteUploadModal.js
import React, { useState, useRef } from 'react'
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Typography, LinearProgress, Box,
  FormControl, InputLabel, Select, MenuItem
} from '@material-ui/core'
import CloudUploadIcon from '@material-ui/icons/CloudUpload'
import DescriptionIcon from '@material-ui/icons/Description'
import axios from 'axios'

export default function QuoteUploadModal ({ open, projectId, modules, onClose, onComplete, documentType = 'quote' }) {
  const [file, setFile] = useState(null)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState(null)
  const [selectedModuleId, setSelectedModuleId] = useState('')
  const fileInputRef = useRef()

  const docLabel = documentType === 'invoice' ? 'Invoice' : 'Quote'

  const handleFileSelect = (e) => {
    const selected = e.target.files[0]
    const allowed = ['application/pdf', 'text/csv', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.ms-excel', 'image/png', 'image/jpeg']
    if (selected && allowed.includes(selected.type)) {
      setFile(selected)
      setError(null)
    } else {
      setError('Please select a PDF, CSV, Excel, or image file')
    }
  }

  const handleUpload = async () => {
    if (!file) return
    setUploading(true)
    setError(null)

    try {
      const formData = new FormData()
      formData.append('quoteFile', file)
      formData.append('projectId', projectId)
      if (selectedModuleId) formData.append('moduleId', selectedModuleId)
      formData.append('documentType', documentType)

      const { data } = await axios.post('/api/bid-estimation/vendor-quotes/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000 // 60s — parsing can take a moment
      })

      setFile(null)
      onComplete(data)
    } catch (e) {
      const msg = e.response?.data?.error || e.message || 'Upload failed'
      setError(msg)
    }
    setUploading(false)
  }

  const handleClose = () => {
    if (uploading) return
    setFile(null)
    setError(null)
    setSelectedModuleId('')
    onClose()
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth='sm' fullWidth>
      <DialogTitle>{`Upload Vendor ${docLabel}`}</DialogTitle>
      <DialogContent>
        <Box
          display='flex'
          flexDirection='column'
          alignItems='center'
          p={3}
          style={{
            border: '2px dashed #ccc',
            borderRadius: 8,
            cursor: uploading ? 'default' : 'pointer',
            backgroundColor: file ? '#f0f7ff' : '#fafafa'
          }}
          onClick={() => !uploading && fileInputRef.current?.click()}
        >
          <input
            ref={fileInputRef}
            type='file'
            accept='.pdf,.csv,.xlsx,.png,.jpg,.jpeg'
            style={{ display: 'none' }}
            onChange={handleFileSelect}
          />
          {file ? (
            <>
              <DescriptionIcon style={{ fontSize: 48, color: '#1976d2' }} />
              <Typography variant='body1' style={{ marginTop: 8 }}>{file.name}</Typography>
              <Typography variant='caption' color='textSecondary'>
                {(file.size / 1024 / 1024).toFixed(2)} MB
              </Typography>
            </>
          ) : (
            <>
              <CloudUploadIcon style={{ fontSize: 48, color: '#999' }} />
              <Typography variant='body1' color='textSecondary' style={{ marginTop: 8 }}>
                {`Click to select a ${docLabel.toLowerCase()} file (PDF, Excel, CSV, or image)`}
              </Typography>
              <Typography variant='caption' color='textSecondary'>Max 10MB</Typography>
            </>
          )}
        </Box>

        {modules && modules.length > 0 && (
          <FormControl fullWidth size="small" variant="outlined" style={{ marginTop: 16 }}>
            <InputLabel>Assign to Module (optional)</InputLabel>
            <Select value={selectedModuleId} onChange={e => setSelectedModuleId(e.target.value)} label="Assign to Module (optional)">
              <MenuItem value="">None</MenuItem>
              {modules.map(m => (
                <MenuItem key={m._id} value={m._id}>{m.scope}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {uploading && (
          <Box mt={2}>
            <LinearProgress />
            <Typography variant='caption' color='textSecondary' align='center' display='block' mt={1}>
              Uploading and parsing...
            </Typography>
          </Box>
        )}

        {error && (
          <Typography color='error' variant='body2' style={{ marginTop: 16 }}>
            {error}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={uploading}>Cancel</Button>
        <Button
          onClick={handleUpload}
          color='primary'
          variant='contained'
          disabled={!file || uploading}
        >
          {uploading ? 'Processing...' : 'Upload & Parse'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
